/**
 * ActivationHero — first-run empty-state hero on the V3 Dashboard.
 *
 * Renders in place of the AI Briefing hero when the user has zero
 * activation completed. Three CTAs prioritized by speed-to-first-value:
 *   1. Analyze an address  →  /v3/properties (fastest, no data needed)
 *   2. Buy your first lead  →  /v3/crm/marketplace (leads-on-demand)
 *   3. Import from CSV      →  /v3/crm/leads (bring-your-own-list)
 */
import { Link } from "react-router-dom";
import { Sparkles, MapPin, ShoppingCart, Upload, ArrowRight } from "lucide-react";

export default function ActivationHero({ firstName }) {
  return (
    <section
      data-testid="v3-activation-hero"
      className="relative overflow-hidden rounded-2xl border border-amber-200/60 shadow-lg"
    >
      {/* Branded background */}
      <div className="absolute inset-0 pointer-events-none"
           style={{
             backgroundImage:
               "radial-gradient(circle at 8% 12%, rgba(245,158,11,0.28), transparent 45%), " +
               "radial-gradient(circle at 92% 0%, rgba(139,92,246,0.22), transparent 50%), " +
               "radial-gradient(circle at 100% 100%, rgba(16,185,129,0.14), transparent 55%)",
           }} />
      <div className="absolute inset-0 bg-white/60 backdrop-blur-2xl pointer-events-none" />

      <div className="relative p-6 md:p-10 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
        {/* Left — pitch */}
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
                style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)" }}>
            <Sparkles className="w-3 h-3" /> Welcome
          </span>
          <h1 className="mt-3 text-[28px] md:text-[38px] font-bold tracking-tight text-slate-900 leading-tight">
            Welcome{firstName ? <>, <span className="text-amber-600">{firstName}</span></> : null}.
            <br className="hidden md:block" />
            <span className="text-slate-700">Let&apos;s find your first deal in 60 seconds.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-[15px] md:text-base text-slate-600 leading-relaxed">
            Pick any of the three starting points below. Each one puts a real property
            in front of you inside a minute — no forms, no drip lessons, no filler.
          </p>
        </div>

        {/* Right — three CTAs stacked */}
        <div className="flex flex-col gap-2.5">
          <ActionCard
            to="/v3/properties"
            testid="activation-cta-analyze"
            icon={MapPin}
            gradient="linear-gradient(135deg, #F59E0B, #EA580C)"
            title="Analyze an address"
            blurb="Type any US property address. AI returns MAO, ARV, and a GO / MAYBE / PASS verdict."
            eta="~30 sec"
          />
          <ActionCard
            to="/v3/crm/marketplace"
            testid="activation-cta-marketplace"
            icon={ShoppingCart}
            gradient="linear-gradient(135deg, #8B5CF6, #6D28D9)"
            title="Buy your first lead"
            blurb="Redeem a filtered BatchData lead — absentee, tax-delinquent, or pre-foreclosure."
            eta="~1 min"
          />
          <ActionCard
            to="/v3/crm/leads"
            testid="activation-cta-import"
            icon={Upload}
            gradient="linear-gradient(135deg, #10B981, #059669)"
            title="Import from CSV"
            blurb="Bring your existing seller list — auto-enriches and scores every row on upload."
            eta="~2 min"
          />
        </div>
      </div>
    </section>
  );
}

function ActionCard({ to, testid, icon: Icon, gradient, title, blurb, eta }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white/90 backdrop-blur-sm px-4 py-3 shadow-sm hover:shadow-lg hover:border-slate-300 transition-all"
    >
      <div className="h-10 w-10 rounded-lg grid place-items-center text-white shadow-md shrink-0 group-hover:scale-105 transition-transform"
           style={{ backgroundImage: gradient }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <div className="text-[14px] font-bold text-slate-900">{title}</div>
          <span className="text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
            {eta}
          </span>
        </div>
        <div className="text-[12px] text-slate-500 leading-snug line-clamp-2">{blurb}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all shrink-0" />
    </Link>
  );
}
